import { useState } from "react";

import type { WorkloadEvent } from "../../api/types";
import { formatClock, formatDateTime } from "../../lib/format";

/** The one kind a long run repeats, and the only kind that is ever folded. */
const REPEATED_KIND = "progress";

interface EventTimelineProps {
  events: WorkloadEvent[];
}

/** One rendered line, and how many of its kind stand behind it. */
interface TimelineRow {
  event: WorkloadEvent;
  /** Where this event sits in the list served — the key, and what "last" is. */
  index: number;
  /** How many consecutive events this line stands for; 1 when it is itself. */
  folded: number;
  /** First index of the stretch it belongs to: what folding is keyed on. */
  group: number;
}

/**
 * Fold each consecutive stretch of repeated events onto its newest line.
 *
 * Only `progress` folds, and only where several of them are adjacent: a
 * `node_requested`, an `expiring` or a `failed` between two of them ends the
 * stretch and is always shown on its own line. Nothing is dropped — the newest
 * of a stretch stands for the rest until somebody asks for them, and the count
 * says how many that is.
 */
function fold(
  events: WorkloadEvent[],
  expanded: ReadonlySet<number>,
): TimelineRow[] {
  const rows: TimelineRow[] = [];
  let cursor = 0;
  while (cursor < events.length) {
    if (events[cursor].kind !== REPEATED_KIND) {
      rows.push({ event: events[cursor], index: cursor, folded: 1, group: cursor });
      cursor += 1;
      continue;
    }
    let end = cursor;
    while (events[end + 1]?.kind === REPEATED_KIND) {
      end += 1;
    }
    const count = end - cursor + 1;
    if (count > 1 && !expanded.has(cursor)) {
      rows.push({ event: events[end], index: end, folded: count, group: cursor });
    } else {
      for (let step = cursor; step <= end; step += 1) {
        rows.push({
          event: events[step],
          index: step,
          // The button rides the newest line of the stretch, so opening and
          // closing it happen in the same place.
          folded: step === end ? count : 1,
          group: cursor,
        });
      }
    }
    cursor = end + 1;
  }
  return rows;
}

/**
 * The trace of what happened, in the words the server used.
 *
 * This is how the product explains itself: every state change appends one
 * plain sentence, and the UI shows them verbatim rather than paraphrasing.
 *
 * A run of a thousand tasks writes a line every time its finished count moves,
 * and four hundred of them are a trace nobody reads to the end. So a
 * **consecutive** stretch of those lines is shown as its newest one with a
 * counted button beside it, and the rest are one click away — in place, in the
 * order and the words they were written in. **No message is ever dropped**, and
 * no other kind is ever inside a fold: the line saying a machine was started,
 * or that the deadline is coming, is always on the screen.
 *
 * Args:
 *     events: Events as served, oldest first.
 *
 * Returns:
 *     An ordered timeline.
 */
export function EventTimeline({ events }: EventTimelineProps): JSX.Element {
  const [expanded, setExpanded] = useState<ReadonlySet<number>>(new Set());

  if (events.length === 0) {
    return <p className="timeline__empty">Nothing has happened yet.</p>;
  }

  const toggle = (group: number): void => {
    setExpanded((current) => {
      const next = new Set(current);
      if (!next.delete(group)) {
        next.add(group);
      }
      return next;
    });
  };

  return (
    <ol className="timeline">
      {fold(events, expanded).map((row) => (
        <li
          className="timeline__item"
          data-kind={row.event.kind}
          data-last={row.index === events.length - 1 ? "true" : "false"}
          key={`${row.event.at}-${row.index}`}
        >
          <span className="timeline__marker" aria-hidden="true" />
          <time
            className="timeline__time mono"
            dateTime={row.event.at}
            title={formatDateTime(row.event.at)}
          >
            {formatClock(row.event.at)}
          </time>
          <span className="timeline__message">
            {row.event.message}
            {row.folded > 1 ? (
              <button
                type="button"
                className="timeline__more"
                onClick={() => toggle(row.group)}
              >
                {expanded.has(row.group)
                  ? `fold these ${row.folded} steps`
                  : `show every step (${row.folded})`}
              </button>
            ) : null}
          </span>
        </li>
      ))}
    </ol>
  );
}
